import { supabase } from '../lib/supabase';

/**
 * Permission Service
 * Handles all permission-related operations including:
 * - Fetching user permissions
 * - Checking individual permissions
 * - Setting permission overrides
 * - Getting manageable permissions for permission management UI
 */
export const permissionService = {
  /**
   * Get all permissions for the current user in a company
   * @param {string} companyId - Company UUID
   * @returns {Promise<{success: boolean, permissions: Array, role: string}>}
   */
  async getUserPermissions(companyId) {
    const { data, error } = await supabase.rpc('get_user_permissions', {
      p_company_id: companyId
    });

    if (error) {
      console.error('Error fetching permissions:', error);
      throw new Error('Failed to fetch permissions');
    }

    if (!data?.success) {
      throw new Error(data?.error || 'Failed to fetch permissions');
    }

    return {
      success: true,
      permissions: data.permissions || [],
      role: data.role,
      userId: data.user_id,
      companyId: data.company_id
    };
  },

  /**
   * Get permissions for a specific user (for management UI)
   * @param {string} userId - Target user UUID
   * @param {string} companyId - Company UUID
   * @returns {Promise<{success: boolean, permissions: Array, role: string}>}
   */
  async getUserPermissionsById(userId, companyId) {
    const { data, error } = await supabase.rpc('get_user_permissions', {
      p_user_id: userId,
      p_company_id: companyId
    });

    if (error) {
      console.error('Error fetching user permissions:', error);
      throw new Error('Failed to fetch user permissions');
    }

    if (!data?.success) {
      throw new Error(data?.error || 'Failed to fetch user permissions');
    }

    return {
      success: true,
      permissions: data.permissions || [],
      role: data.role,
      userId: data.user_id,
      companyId: data.company_id
    };
  },

  /**
   * Check if the current user has a specific permission
   * Uses local cache when available, falls back to RPC
   * @param {string} companyId - Company UUID
   * @param {string} permissionCode - Permission code to check
   * @returns {Promise<boolean>}
   */
  async checkPermission(companyId, permissionCode) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return false;

    const { data, error } = await supabase.rpc('check_user_permission', {
      p_user_id: user.id,
      p_company_id: companyId,
      p_permission_code: permissionCode
    });

    if (error) {
      console.error('Error checking permission:', error);
      return false;
    }

    return data === true;
  },

  /**
   * Get list of permissions the caller can modify for a target user
   * @param {string} targetUserId - Target user UUID
   * @param {string} companyId - Company UUID
   * @returns {Promise<{success: boolean, permissions: Array, canManageAny: boolean}>}
   */
  async getManageablePermissions(targetUserId, companyId) {
    const { data, error } = await supabase.rpc('get_manageable_permissions', {
      p_target_user_id: targetUserId,
      p_company_id: companyId
    });

    if (error) {
      console.error('Error fetching manageable permissions:', error);
      throw new Error('Failed to fetch manageable permissions');
    }

    if (!data?.success) {
      throw new Error(data?.error || 'Failed to fetch manageable permissions');
    }

    return {
      success: true,
      permissions: data.permissions || [],
      canManageAny: data.can_manage_any,
      targetUserId: data.target_user_id,
      targetRole: data.target_role
    };
  },

  /**
   * Set a permission override for a user
   * @param {string} targetUserId - User whose permission to modify
   * @param {string} companyId - Company context
   * @param {string} permissionCode - Permission code to modify
   * @param {boolean} granted - true to grant, false to revoke
   * @param {string} reason - Optional reason for the change
   * @returns {Promise<{success: boolean, action: string}>}
   */
  async setPermissionOverride(targetUserId, companyId, permissionCode, granted, reason = null) {
    const { data, error } = await supabase.rpc('set_user_permission_override', {
      p_target_user_id: targetUserId,
      p_company_id: companyId,
      p_permission_code: permissionCode,
      p_granted: granted,
      p_reason: reason
    });

    if (error) {
      console.error('Error setting permission override:', error);
      throw new Error('Failed to set permission override');
    }

    if (!data?.success) {
      throw new Error(data?.error || 'Failed to set permission override');
    }

    return {
      success: true,
      action: data.action,
      permissionCode: data.permission_code,
      granted: data.granted
    };
  },

  /**
   * Remove a permission override (revert to role default)
   * @param {string} targetUserId - User whose override to remove
   * @param {string} companyId - Company context
   * @param {string} permissionCode - Permission code
   * @returns {Promise<{success: boolean}>}
   */
  async removePermissionOverride(targetUserId, companyId, permissionCode) {
    const { data, error } = await supabase.rpc('set_user_permission_override', {
      p_target_user_id: targetUserId,
      p_company_id: companyId,
      p_permission_code: permissionCode,
      p_granted: null,  // null removes the override
      p_reason: null
    });

    if (error) {
      console.error('Error removing permission override:', error);
      throw new Error('Failed to remove permission override');
    }

    if (!data?.success) {
      throw new Error(data?.error || 'Failed to remove permission override');
    }

    return {
      success: true,
      action: 'removed'
    };
  },

  /**
   * Get all available permissions (master list)
   * @returns {Promise<Array>}
   */
  async getAllPermissions() {
    const { data, error } = await supabase
      .from('permissions')
      .select('*')
      .order('category')
      .order('code');

    if (error) {
      console.error('Error fetching all permissions:', error);
      throw new Error('Failed to fetch permissions list');
    }

    return data || [];
  },

  /**
   * Permission codes grouped by category for UI display
   */
  PERMISSION_CATEGORIES: {
    workforce: {
      label: 'Workforce',
      permissions: ['view_employees', 'view_contractors', 'manage_employees', 'manage_contractors', 'manage_permissions']
    },
    jobs: {
      label: 'Jobs',
      permissions: ['view_all_jobs', 'view_assigned_jobs', 'view_public_jobs', 'create_jobs', 'manage_jobs', 'assign_jobs', 'manage_job_templates']
    },
    reports: {
      label: 'Reports',
      permissions: ['view_reports', 'view_earnings', 'export_reports', 'view_audit_logs']
    },
    dashboard: {
      label: 'Dashboard',
      permissions: ['view_dashboard_metrics', 'view_own_metrics']
    },
    settings: {
      label: 'Settings',
      permissions: ['manage_company_settings', 'manage_invoice_settings', 'manage_notifications', 'view_billing', 'export_data']
    }
  }
};
